/**
 * Turn AH — Privacy Policy.
 *
 * Static, single-file page. Public (rendered outside <Shelled>) so it
 * remains reachable without login — legal requirement for GDPR / CCPA.
 * Zero backend / DB touch, zero new dependency.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Cookie,
  Database,
  FileText,
  Globe,
  Lock,
  Mail,
  RefreshCw,
  Server,
  Shield,
  UserCheck,
  Users,
  Clock,
  Baby,
} from "lucide-react";

const LAST_UPDATED = "2026-09-04";
const CONTACT_EMAIL = "privacy@beyondactiv.com";

const SECTIONS = [
  { id: "introduction",       icon: FileText,   label: "Introduction" },
  { id: "data-we-collect",    icon: Database,   label: "Data We Collect" },
  { id: "how-we-use",         icon: Server,     label: "How We Use Your Data" },
  { id: "third-parties",      icon: Users,      label: "Third-Party Services" },
  { id: "retention",          icon: Clock,      label: "Data Retention" },
  { id: "rights",             icon: UserCheck,  label: "Your Rights" },
  { id: "security",           icon: Lock,       label: "Security" },
  { id: "cookies",            icon: Cookie,     label: "Cookies" },
  { id: "international",      icon: Globe,      label: "International Transfers" },
  { id: "children",           icon: Baby,       label: "Children" },
  { id: "changes",            icon: RefreshCw,  label: "Changes to This Policy" },
  { id: "contact",            icon: Mail,       label: "Contact" },
];

function SectionTitle({ icon: Icon, id, children }) {
  return (
    <h2
      id={id}
      className="text-xl font-medium text-hm-fg mt-12 mb-4 flex items-center gap-2.5 scroll-mt-24"
      data-testid={`privacy-h2-${id}`}
    >
      <Icon className="h-4 w-4 text-hm-accent" />
      {children}
    </h2>
  );
}

export default function PrivacyPolicy() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Privacy Policy — Hail Mary Intelligence OS";
    const prevDesc = document.querySelector('meta[name="description"]')?.content;
    let metaEl = document.querySelector('meta[name="description"]');
    if (!metaEl) {
      metaEl = document.createElement("meta");
      metaEl.name = "description";
      document.head.appendChild(metaEl);
    }
    metaEl.content =
      "How Hail Mary Intelligence OS collects, uses, and protects your data.";
    return () => {
      document.title = prevTitle;
      if (metaEl && prevDesc !== undefined) metaEl.content = prevDesc;
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-hm-bg text-hm-fg"
      data-testid="privacy-page"
    >
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-hm-bg/95 backdrop-blur border-b border-hm-border">
        <div className="max-w-[1100px] mx-auto px-6 h-14 flex items-center justify-between">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-hm-fg-muted hover:text-hm-fg transition-colors"
            data-testid="privacy-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to app
          </Link>
          <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
            Last updated · <span className="text-hm-fg-muted">{LAST_UPDATED}</span>
          </div>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-14 max-w-[720px]">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-hm-border bg-hm-bg-1 mono text-[10px] uppercase tracking-widest text-hm-accent mb-4">
            <Shield className="h-3 w-3" />
            Legal
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-hm-fg leading-tight">
            Privacy Policy
          </h1>
          <p className="text-hm-fg-muted mt-3 text-sm leading-relaxed">
            Hail Mary Intelligence OS is an agentic intelligence platform
            operated by Beyond Activ. This document explains, in plain
            language, what data we collect, why we collect it, who we share
            it with, and how you can exercise your rights over it.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
          {/* TOC — mobile accordion + desktop sticky */}
          <aside className="lg:sticky lg:top-24 lg:self-start" data-testid="privacy-toc">
            <details className="lg:open:!block group" open>
              <summary
                className="lg:hidden flex items-center justify-between cursor-pointer px-3 py-2.5 border border-hm-border rounded-md bg-hm-bg-1 mono text-[11px] uppercase tracking-widest text-hm-fg-muted"
              >
                <span>Table of contents</span>
                <span className="group-open:rotate-90 transition-transform">›</span>
              </summary>
              <nav className="mt-3 lg:mt-0">
                <div className="hidden lg:block mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mb-3 px-1">
                  On this page
                </div>
                <ul className="space-y-0.5">
                  {SECTIONS.map(({ id, icon: Icon, label }) => (
                    <li key={id}>
                      <a
                        href={`#${id}`}
                        className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs text-hm-fg-muted hover:text-hm-fg hover:bg-hm-bg-1 transition-colors"
                        data-testid={`privacy-toc-${id}`}
                      >
                        <Icon className="h-3.5 w-3.5 opacity-60" />
                        <span className="truncate">{label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </details>
          </aside>

          {/* Main content */}
          <article
            className="max-w-[720px] text-sm text-hm-fg-muted leading-relaxed"
            data-testid="privacy-body"
          >
            {/* 1. Introduction */}
            <SectionTitle icon={FileText} id="introduction">1. Introduction</SectionTitle>
            <p>
              Hail Mary Intelligence OS ("Hail Mary", "we", "us") is an
              enterprise SaaS platform operated by Beyond Activ. We build
              an agentic orchestration layer that helps operators discover,
              curate, and reason over external intelligence: daily briefs,
              curated articles, chat conversations with an assistant, and
              messages relayed from a WhatsApp-based curator workflow
              ("Wingman").
            </p>
            <p>
              This policy applies to every product surface hosted under the
              domains <code className="mono text-[12px] text-hm-fg">btba50ai.online</code>{" "}
              and{" "}
              <code className="mono text-[12px] text-hm-fg">*.preview.emergentagent.com</code>.
              By using Hail Mary, you agree to the practices described
              below. If you do not agree, do not use the service.
            </p>

            {/* 2. Data we collect */}
            <SectionTitle icon={Database} id="data-we-collect">2. Data We Collect</SectionTitle>
            <h3 className="text-hm-fg font-medium mt-6 mb-2 text-sm">2.1 Identity &amp; access data</h3>
            <p>
              When you sign in with Google, we receive from Google OAuth
              your <strong>email address</strong>, <strong>display name</strong>, and
              a <strong>profile picture URL</strong>. We do not receive your
              Google password. We use these values to identify you across
              sessions and to display your identity in the UI.
            </p>
            <h3 className="text-hm-fg font-medium mt-6 mb-2 text-sm">2.2 Usage &amp; content data</h3>
            <ul className="list-disc pl-5 space-y-1.5 my-2">
              <li>Daily briefs generated for you (dates, article counts, article URLs, LLM traces).</li>
              <li>Chat messages you exchange with the Hail Mary assistant (user turns, assistant turns, timestamps, model used).</li>
              <li>Messages relayed from Wingman via WhatsApp (text body, sender phone number, WhatsApp message id, media files).</li>
              <li>Attachments you or Wingman send: files, images, documents, PDFs. Stored in a private Supabase Storage bucket with short-lived signed URLs.</li>
              <li>Watchlist configuration: companies, topics, regions, and sources you follow.</li>
            </ul>
            <h3 className="text-hm-fg font-medium mt-6 mb-2 text-sm">2.3 Technical data</h3>
            <p>
              For each request, we log the HTTP method, path, response
              status, latency in milliseconds, an anonymous request ID, and
              your public IP address (retained 90 days for abuse detection
              and debugging). We also process a session cookie tied to your
              authenticated session.
            </p>

            {/* 3. How we use */}
            <SectionTitle icon={Server} id="how-we-use">3. How We Use Your Data</SectionTitle>
            <ul className="list-disc pl-5 space-y-1.5 my-2">
              <li><strong>Deliver the service.</strong> Authenticate you, generate briefs, run the assistant, relay Wingman messages, host attachments, and render the UI.</li>
              <li><strong>Improve orchestration.</strong> Aggregate, non-identifying signals (e.g., token counts, retry rates, latency histograms) help us tune agents, prompts, and models. We do not train third-party models on your content.</li>
              <li><strong>Security &amp; abuse detection.</strong> Detect brute-force attempts, replay attacks, and pipeline failures.</li>
              <li><strong>Legal obligations.</strong> Comply with applicable laws, court orders, and lawful requests from authorities.</li>
            </ul>
            <p>
              We do <strong>not</strong> sell your data. We do{" "}
              <strong>not</strong> run advertising or third-party analytics
              on the platform.
            </p>

            {/* 4. Third parties */}
            <SectionTitle icon={Users} id="third-parties">4. Third-Party Services</SectionTitle>
            <p>
              To operate the platform we rely on the following processors.
              Each provider is bound by its own data-processing agreement.
            </p>
            <div className="my-4 rounded-lg border border-hm-border bg-hm-bg-1 divide-y divide-hm-border">
              {[
                { name: "Supabase", role: "PostgreSQL database + object storage", locality: "Data hosted in the EU region." },
                { name: "Emergent LLM Gateway", role: "Inference routing to OpenAI (GPT-4o), Anthropic (Claude), and Google (Gemini / Nano Banana)", locality: "Provider processes chat prompts to generate responses. Prompts are not used to train models." },
                { name: "Meta WhatsApp Business Cloud API", role: "Inbound and outbound WhatsApp messaging bridge", locality: "Subject to Meta's WhatsApp Business Data Policy." },
                { name: "Cloudflare", role: "CDN, TLS termination, DDoS protection", locality: "Global anycast network. Logs limited to network telemetry." },
                { name: "ScraperAPI & Serper API", role: "Public-web content discovery for daily briefs", locality: "We only send public search queries and target URLs. No personal data is transmitted." },
                { name: "Emergent Platform", role: "Application hosting and deployment (preview & production)", locality: "Managed cloud runtime." },
              ].map((row) => (
                <div key={row.name} className="p-4 flex flex-col sm:flex-row gap-2 sm:items-baseline">
                  <div className="text-hm-fg font-medium text-sm sm:w-52 shrink-0">{row.name}</div>
                  <div className="text-xs text-hm-fg-muted flex-1">
                    <div>{row.role}</div>
                    <div className="text-hm-fg-subtle mt-0.5">{row.locality}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* 5. Retention */}
            <SectionTitle icon={Clock} id="retention">5. Data Retention</SectionTitle>
            <ul className="list-disc pl-5 space-y-1.5 my-2">
              <li><strong>Chat messages</strong> (user, assistant, Wingman): retained <strong>12 months</strong> from creation, then purged unless referenced by an archived brief.</li>
              <li><strong>Daily briefs and their article records</strong>: retained indefinitely as a business archive, so historical KPIs remain reproducible.</li>
              <li><strong>Uploaded files &amp; media</strong>: signed URLs live <strong>7 days</strong>; the underlying object stays in cold storage aligned to the parent record's retention.</li>
              <li><strong>Request &amp; audit logs</strong>: retained <strong>90 days</strong>.</li>
              <li><strong>Deleted account</strong>: erasure completed within <strong>30 days</strong> of a verified request (see §6).</li>
            </ul>

            {/* 6. Rights */}
            <SectionTitle icon={UserCheck} id="rights">6. Your Rights (GDPR &amp; CCPA)</SectionTitle>
            <p>
              If you are located in the European Economic Area, the United
              Kingdom, Switzerland, or a US state with equivalent
              legislation (California, Colorado, Connecticut, Virginia,
              Utah), you have the following rights:
            </p>
            <div className="my-4 rounded-lg border border-hm-border bg-hm-bg-1 p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
              {[
                ["Access", "Request a copy of the data we hold about you."],
                ["Rectification", "Have inaccurate data corrected."],
                ["Erasure", "Ask us to delete your data (right to be forgotten)."],
                ["Portability", "Receive your data in a machine-readable format."],
                ["Restriction", "Limit how we process your data."],
                ["Objection", "Oppose specific processing activities."],
              ].map(([r, d]) => (
                <div key={r}>
                  <div className="text-hm-fg font-medium">{r}</div>
                  <div className="text-hm-fg-muted mt-0.5">{d}</div>
                </div>
              ))}
            </div>
            <p>
              To exercise any of these rights, email{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-hm-accent hover:underline">
                {CONTACT_EMAIL}
              </a>{" "}
              from the address associated with your account. We respond
              within 30 days.
            </p>

            {/* 7. Security */}
            <SectionTitle icon={Lock} id="security">7. Security</SectionTitle>
            <ul className="list-disc pl-5 space-y-1.5 my-2">
              <li>All traffic is encrypted end-to-end with <strong>TLS 1.2+</strong>.</li>
              <li>Data at rest is encrypted at the storage layer (Supabase managed encryption).</li>
              <li>Secrets (API keys, OAuth client secrets, database credentials) live in environment variables — never in source code, never in logs.</li>
              <li>Database access is protected by <strong>row-level security</strong> policies and a service-role key restricted to backend workloads.</li>
              <li>Every request is traced by an anonymous request ID for post-hoc investigation without exposing user content in aggregate logs.</li>
              <li>We publish incident notes to affected accounts within 72 hours of confirmation, per GDPR Article 33.</li>
            </ul>

            {/* 8. Cookies */}
            <SectionTitle icon={Cookie} id="cookies">8. Cookies</SectionTitle>
            <p>
              We use only <strong>essential cookies</strong>: an
              HttpOnly-secure session cookie set at login, and a
              short-lived platform-access cookie for the preview
              environment. We do not run analytics cookies, ad cookies, or
              social tracking pixels. Cloudflare may set a{" "}
              <code className="mono text-[12px] text-hm-fg">__cf_bm</code> bot-mitigation cookie
              (30 min TTL) as part of its DDoS protection — this cookie is
              non-identifying and expires automatically.
            </p>

            {/* 9. International transfers */}
            <SectionTitle icon={Globe} id="international">9. International Transfers</SectionTitle>
            <p>
              Your data is primarily hosted in the European Union
              (Supabase EU region). Some sub-processors (Emergent LLM
              Gateway, Meta WhatsApp Cloud API, Cloudflare, OpenAI,
              Anthropic, Google) operate globally, which may involve
              transfers to the United States or other jurisdictions. These
              transfers are covered by <strong>Standard Contractual
              Clauses</strong> and, where applicable, supplementary
              measures aligned with the EDPB recommendations.
            </p>

            {/* 10. Children */}
            <SectionTitle icon={Baby} id="children">10. Children</SectionTitle>
            <p>
              Hail Mary is a professional-tools platform not intended for
              individuals under the age of 16. We do not knowingly collect
              personal data from children. If you believe a child has
              submitted personal data, contact us and we will delete it.
            </p>

            {/* 11. Changes */}
            <SectionTitle icon={RefreshCw} id="changes">11. Changes to This Policy</SectionTitle>
            <p>
              We will notify active users of any material change to this
              policy at least <strong>30 days</strong> before it takes
              effect, via email to the address on file and via an in-app
              banner. Non-material changes (typos, clarifications) may be
              rolled out immediately; the "Last updated" date at the top
              of this page always reflects the latest revision.
            </p>

            {/* 12. Contact */}
            <SectionTitle icon={Mail} id="contact">12. Contact</SectionTitle>
            <p>
              For privacy questions, data-subject requests, or to reach
              our Data Protection Officer:
            </p>
            <div className="my-4 rounded-lg border border-hm-border bg-hm-bg-1 p-4 text-xs">
              <div className="text-hm-fg font-medium mb-1">Beyond Activ — Privacy team</div>
              <div className="text-hm-fg-muted">
                Email:{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-hm-accent hover:underline">
                  {CONTACT_EMAIL}
                </a>
              </div>
              <div className="text-hm-fg-muted">
                Data Protection Officer: <span className="text-hm-fg-subtle italic">to be appointed</span>
              </div>
            </div>

            {/* Footer actions */}
            <div className="mt-14 pt-6 border-t border-hm-border flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-hm-border bg-hm-bg-1 hover:border-hm-border-strong text-xs text-hm-fg-muted hover:text-hm-fg transition-colors"
                data-testid="privacy-contact-button"
              >
                <Mail className="h-3.5 w-3.5" />
                Contact privacy@beyondactiv.com
              </a>
              <a
                href={`mailto:${CONTACT_EMAIL}?subject=Data%20Deletion%20Request`}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-hm-border bg-hm-bg-1 hover:border-hm-danger text-xs text-hm-fg-muted hover:text-hm-danger transition-colors"
                data-testid="privacy-deletion-button"
              >
                <Shield className="h-3.5 w-3.5" />
                Data deletion request
              </a>
            </div>

            <p className="mt-8 mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
              © {new Date().getFullYear()} Beyond Activ · Hail Mary Intelligence OS · v0.0.1-phase0
            </p>
          </article>
        </div>
      </div>
    </div>
  );
}
